import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Button,
    Card,
    Descriptions,
    Drawer,
    Grid,
    Input,
    message,
    Modal,
    Popconfirm,
    Radio,
    Select,
    Space,
    Switch,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    ReloadOutlined,
    TeamOutlined,
    ThunderboltOutlined
} from '@ant-design/icons';
import {
    applyEventUpdate,
    createEvent,
    deleteEvent,
    deleteEventBatch,
    getDbListenersByEventId,
    getEvents,
    getListenersByEvent,
    updateEvent,
    updateListenerUse
} from '../api/systemEventApi';
import ManagementHeader from './ManagementHeader';
import useApiPermission from '../hooks/useApiPermission';
import PermissionButton from './common/PermissionButton';
import {pickList} from '../api/http';
import GlobalListenersTab from './GlobalListenersTab';
import CompareListenerModal from './CompareListenerModal';

const EventManagement = () => {
  const screens = Grid.useBreakpoint();
  const [activeTab, setActiveTab] = useState('events');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [bulkRegistering, setBulkRegistering] = useState(false);

  // 리스너 탭 헤더 액션 상태(상단 타이틀 영역에 표시)
  const [listenerHeaderActions, setListenerHeaderActions] = useState(null);

  const [selected, setSelected] = useState(null);
  const [editExpln, setEditExpln] = useState('');
  const [editUseYn, setEditUseYn] = useState('Y');
  const [editDispatchType, setEditDispatchType] = useState('SYNC');
  // 추가: Drawer 열림 상태 및 검색어 상태
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [nameQuery, setNameQuery] = useState('');
  const [explnQuery, setExplnQuery] = useState('');

  const [listenersLoading, setListenersLoading] = useState(false);
  const [listeners, setListeners] = useState([]);
  const [listenersEventId, setListenersEventId] = useState(null);
  const [dbListeners, setDbListeners] = useState([]);
  const [dbListenersLoading, setDbListenersLoading] = useState(false);
  const [unifiedListeners, setUnifiedListeners] = useState([]);

  const [filterMode, setFilterMode] = useState('all'); // all | changed | unregistered | registered | deleted
  // 선택갱신 진행 상태
  const [applyLoading, setApplyLoading] = useState(false);

  // 비교 모달 상태
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareRow, setCompareRow] = useState(null);

  // 비교 모달 핸들러들 (openCompare는 현재 사용되지 않아 제거)
  const closeCompare = () => { setCompareOpen(false); setCompareRow(null); };
  const onComparedApplied = async () => { await refreshDbListeners(); };

  const { canCall, loading: permLoading } = useApiPermission();
  const canRead = canCall(getEvents);
  const canUpdate = canCall(updateEvent);
  const canReadListeners = canCall(getListenersByEvent);
  const canReadDbListeners = canCall(getDbListenersByEventId);
  const canUpdateListener = canCall(updateListenerUse);
  const canCreateEvent = canCall(createEvent);
  const canDeleteSingle = canCall(deleteEvent);
  const canDeleteMulti = canCall(deleteEventBatch);
  const canApply = canCall(applyEventUpdate);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (permLoading) return;
      if (!canRead) { setData([]); message.warning('이벤트 목록 조회 권한이 없습니다.'); return; }
      // 백엔드에서 registerType과 리스너 수를 포함해 내려주므로 그대로 사용
      const res = await getEvents();
      const list = pickList(res);
      const rows = (Array.isArray(list) ? list : []).map(e => ({
        ...e,
        key: e.eventId || `pv::${e.eventKey}`,
        registerType: e.registerType || (e.eventId ? '등록' : '미등록'),
        runtimeListenerCount: e.runtimeListenerCount ?? 0,
        dbListenerCount: e.dbListenerCount ?? (e.listenerIdList?.length ?? 0),
      }));
      // 정렬: 등록구분 우선(삭제/변경/미등록/등록), 이후 이름
      const rank = (t) => {
        if (t === '삭제') return 0;
        if (t === '변경') return 1;
        if (t === '미등록') return 2;
        if (t === '등록') return 3;
        return 99;
      };
      rows.sort((a,b)=> (rank(a.registerType)-rank(b.registerType)) || (a.name||'').localeCompare(b.name||''));
      setData(rows);
    } catch (e) { message.error('이벤트 목록 조회 실패'); }
    finally { setLoading(false); }
  }, [permLoading, canRead]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Drawer 오픈 핸들러: 선택, 에디트 값 설정, 리스너 로드
  const openDrawer = async (record) => {
    if (!record) return;
    setSelected(record);
    setEditExpln(record.eventExpln || '');
    setEditUseYn(record.useYn || 'Y');
    setEditDispatchType((record.dispatchType || 'SYNC').toUpperCase());
    setDrawerOpen(true);
    // 리스너 로드
    setListenersEventId(record.eventId || null);
    setListenersLoading(true);
    try {
      if (record?.eventKey && canReadListeners) {
        const res = await getListenersByEvent(record.eventKey);
        const list = pickList(res);
        setListeners(Array.isArray(list) ? list : []);
      } else { setListeners([]); }
    } catch (e) { setListeners([]); }
    finally { setListenersLoading(false); }

    if (record?.eventId && canReadDbListeners) {
      setDbListenersLoading(true);
      try {
        const dbRes = await getDbListenersByEventId(record.eventId);
        const dbList = pickList(dbRes);
        setDbListeners(Array.isArray(dbList) ? dbList : []);
      } catch (e) { setDbListeners([]); }
      finally { setDbListenersLoading(false); }
    } else {
      setDbListeners([]);
    }
  };


  const submitEdit = async () => {
    if (!selected) return;
    try {
      if (permLoading || !canUpdate) { message.warning('수정 권한이 없습니다.'); return; }
      await updateEvent(selected.eventId, { eventExpln: editExpln, useYn: editUseYn, dispatchType: editDispatchType });
      message.success('이벤트가 수정되었습니다.');
      setDrawerOpen(false);
      setSelected(null);
      fetchData();
    } catch (e) { message.error('수정 실패: ' + (e?.message || '')); }
  };

  // DB 리스너 토글 핸들러
  const refreshDbListeners = async () => {
    if (!listenersEventId || !canReadDbListeners) return;
    setDbListenersLoading(true);
    try {
      const dbRes = await getDbListenersByEventId(listenersEventId);
      const dbList = pickList(dbRes);
      setDbListeners(Array.isArray(dbList) ? dbList : []);
    } catch (e) {
      setDbListeners([]);
    } finally {
      setDbListenersLoading(false);
    }
  };

  const toggleDbListenerUse = async (row) => {
    try {
      if (permLoading || !canUpdateListener) { message.warning('리스너 수정 권한이 없습니다.'); return; }
      const next = ((row.useYn || 'Y').toUpperCase() === 'Y') ? 'N' : 'Y';
      await updateListenerUse(row.listenerId, next);
      message.success(`리스너 사용여부가 '${next}'로 변경되었습니다.`);
      await refreshDbListeners();
    } catch (e) {
      message.error('리스너 변경 실패');
    }
  };

  // 런타임/DB 리스너 통합 리스트 갱신: drawerOpen 기준
  useEffect(() => {
    if (!drawerOpen) { setUnifiedListeners([]); return; }
    const rt = Array.isArray(listeners) ? listeners : [];
    const db = Array.isArray(dbListeners) ? dbListeners : [];
    const keyOf = (o) => `${o.beanClass}::${o.methodName}`;
    const map = new Map();
    rt.forEach(r => {
      const k = keyOf(r);
      map.set(k, {
        kind:r.kind, beanName:r.beanName, beanClass:r.beanClass, methodName:r.methodName,
        inRuntime:true, inDb:false, listenerId:null, useYn:'Y', dsctn: undefined, rtDesc: (r.description||''),
        eventName: selected?.name || selected?.eventKey || ''
      });
    });
    db.forEach(d => {
      const k = keyOf(d);
      if (map.has(k)) {
        const cur = map.get(k);
        map.set(k, { ...cur, inDb:true, listenerId:d.listenerId, useYn:d.useYn, dsctn: d.dsctn });
      } else {
        map.set(k, {
          kind:d.kind, beanName:d.beanName, beanClass:d.beanClass, methodName:d.methodName,
          inRuntime:false, inDb:true, listenerId:d.listenerId, useYn:d.useYn, dsctn: d.dsctn, rtDesc: '',
          eventName: selected?.name || selected?.eventKey || ''
        });
      }
    });
    const list = Array.from(map.values()).sort((a,b)=> (a.beanClass||'').localeCompare(b.beanClass||'') || (a.methodName||'').localeCompare(b.methodName||''));
    setUnifiedListeners(list);
  }, [drawerOpen, listeners, dbListeners, selected]);

  // 이벤트 등록
  const registerEvent = async (row) => {
    if (!row?.eventKey) return;
    if (permLoading || !canCreateEvent) { message.warning('등록 권한이 없습니다.'); return; }
    try {
      await createEvent({ eventKey: row.eventKey, name: row.name, eventExpln: null });
      message.success('이벤트가 등록되었습니다.');
      fetchData();
    } catch (e) { message.error('이벤트 등록 실패'); }
  };

  const handleDelete = async (row) => {
    if (!row?.eventId) return;
    if (permLoading || !canDeleteSingle) { message.warning('삭제 권한이 없습니다.'); return; }
    try {
      await deleteEvent(row.eventId);
      message.success('삭제되었습니다.');
      fetchData();
    } catch (e) { message.error('삭제 실패'); }
  };

  const handleBatchDelete = () => {
    if (permLoading || !canDeleteMulti) { message.warning('일괄 삭제 권한이 없습니다.'); return; }
    // '삭제' 상태(런타임에는 없고 DB에만 존재)만 대상으로 일괄 삭제
    const ids = data
      .filter(r => selectedKeys.includes(r.key) && (r.registerType||'')==='삭제')
      .map(r => r.eventId)
      .filter(Boolean);
    if (!ids.length) { message.warning("일괄 삭제할 '삭제' 상태의 이벤트를 선택하세요."); return; }
    Modal.confirm({
      title: '선택 삭제',
      content: `삭제 상태의 이벤트 ${ids.length}건을 DB에서 삭제하시겠습니까?`,
      okText: '삭제', okType: 'danger', cancelText: '취소',
      onOk: async () => {
        try {
          await deleteEventBatch(ids);
          message.success('삭제 완료');
          setSelectedKeys([]);
          fetchData();
        } catch (e) {
          message.error('일괄 삭제 실패');
        }
      }
    });
  };

  const handleBatchRegister = () => {
    if (permLoading || !canCreateEvent) { message.warning('등록 권한이 없습니다.'); return; }
    const targets = data.filter(r => selectedKeys.includes(r.key) && !r.eventId);
    if (!targets.length) { message.warning('등록할 미등록 이벤트를 선택하세요.'); return; }
    Modal.confirm({
      title: '선택 등록',
      content: `미등록 이벤트 ${targets.length}개를 등록하시겠습니까?`,
      okText: '등록', cancelText: '취소',
      onOk: async () => {
        setBulkRegistering(true);
        let success = 0; let fail = 0;
        for (const t of targets) {
          try {
            await createEvent({ eventKey: t.eventKey, name: t.name, eventExpln: null });
            success++;
          } catch (_) { fail++; }
        }
        setBulkRegistering(false);
        message.info(`등록 완료: ${success}건 / 실패: ${fail}건`);
        setSelectedKeys([]);
        fetchData();
      }
    });
  };

  // 신규: 선택된 변경 이벤트 일괄 갱신
  const handleBatchApplyUpdate = () => {
    if (permLoading || !canApply) { message.warning('선택갱신 권한이 없습니다.'); return; }
    const targets = data.filter(r => selectedKeys.includes(r.key) && r.eventId && (r.registerType||'')==='변경');
    if (!targets.length) { message.warning('갱신할 변경된 이벤트를 선택하세요.'); return; }
    Modal.confirm({
      title: '선택 갱신',
      content: `변경 상태의 이벤트 ${targets.length}건을 갱신하시겠습니까?`,
      okText: '갱신', cancelText: '취소',
      onOk: async () => {
        setApplyLoading(true);
        try {
          const ids = targets.map(t => t.eventId);
          await applyEventUpdate(ids);
          message.success('선택 이벤트 갱신을 완료했습니다.');
          setSelectedKeys([]);
          fetchData();
        } catch (e) {
          message.error('선택 갱신 실패');
        } finally {
          setApplyLoading(false);
        }
      }
    });
  };


  // 리스너 테이블 컬럼(통합)
  const unifiedListenerColumns = [
    // 1) 등록구분
    { title: '등록구분', key: 'registerType', width: 100, align:'center', render: (_, r) => {
      const rt = (r.rtDesc||'').trim();
      const dbd = (r.dsctn||'').trim();
      const both = r.inRuntime && r.inDb;
      const equal = (rt === dbd);
      const label = both ? (equal ? '등록' : '변경') : '변경';
      const color = label==='변경' ? 'orange' : 'green';
      return <Tag color={color}>{label}</Tag>;
    }},
    // 2) 사용여부
    { title: '사용여부', dataIndex: 'useYn', key: 'useYn', width: 110, align:'center', render: (v, r) => (
      r.inDb ? (
        <Switch checked={(v||'Y').toUpperCase()==='Y'} onChange={() => toggleDbListenerUse({ listenerId:r.listenerId, useYn:r.useYn })} disabled={permLoading || !canUpdateListener} />
      ) : (
        <Switch disabled checked />
      )
    ) },
    // 3) 종류
    { title: '종류', dataIndex: 'kind', key: 'kind', width: 110, render: (k) => <Tag>{k}</Tag> },
    // 5) 설명(우선순위: DB dsctn > 런타임설명)
    { title: '설명', key: 'description', ellipsis: true, render: (_, r) => (r.dsctn || r.rtDesc || '-') },
    // 4) 이벤트명
    { title: '이벤트명', dataIndex: 'eventName', key: 'eventName', width: 220, render: (v) => v || '-' },
    // 6) 클래스
    { title: '클래스', dataIndex: 'beanClass', key: 'beanClass', ellipsis: true },
  ];

  const columns = [
    { title: '등록구분', key: 'registerType', width: 90, align: 'center', 
      sorter: (a, b) => {
        const rank = (t) => { if (t === '삭제') return 0; if (t === '변경') return 1; if (t === '미등록') return 2; if (t === '등록') return 3; return 99; };
        return rank(a.registerType || '') - rank(b.registerType || '');
      },
      render: (_, r) => {
        const label = r.registerType || (r.eventId ? '등록' : '미등록');
        const color = label==='삭제' ? 'volcano' : (label==='미등록' ? 'red' : (label==='변경' ? 'orange' : 'green'));
        return <Tag color={color}>{label}</Tag>;
      }
    },
    { title: '상태', key: 'status', width: 80, align: 'center', 
      sorter: (a, b) => ((a.useYn || 'Y').toUpperCase()).localeCompare((b.useYn || 'Y').toUpperCase()),
      render: (_, r) => (r.eventId ? ((r.useYn || 'Y').toUpperCase() === 'Y' ? <Tag color="green">사용</Tag> : <Tag>중지</Tag>) : <Tag> - </Tag>) 
    },
    { title: '디스패치', dataIndex: 'dispatchType', key: 'dispatchType', width: 80, 
      sorter: (a, b) => ((a.dispatchType || 'SYNC').toUpperCase()).localeCompare((b.dispatchType || 'SYNC').toUpperCase()),
      render: (v, r) => {
        if (!r.eventId) return <Tag>-</Tag>;
        const t = (v || 'SYNC').toUpperCase();
        return t === 'ASYNC_AFTER_COMMIT' ? <Tag color="volcano">비동기(커밋후)</Tag> : (t === 'ASYNC' ? <Tag color="volcano">비동기</Tag> : <Tag color="blue">동기</Tag>);
      }
    },
    // 리스너 컬럼을 런타임 개수로 표시
    { title: '리스너', key: 'listeners', width: 110, align: 'center', 
      sorter: (a, b) => (a.runtimeListenerCount ?? 0) - (b.runtimeListenerCount ?? 0),
      render: (_, r) => (
        <Tag color={(r.registerType==='변경')?'orange':(r.registerType==='삭제'?'volcano':(r.registerType==='미등록'?'red':'geekblue'))}>
          {(r.runtimeListenerCount ?? 0)}
        </Tag>
      ) 
    },
    { title: '설명', dataIndex: 'eventExpln', key: 'eventExpln', ellipsis: true, 
      sorter: (a, b) => (a.eventExpln || '').localeCompare(b.eventExpln || ''),
      render: v => v || '-' 
    },
    { title: '이벤트명', dataIndex: 'name', key: 'name', width: 220, 
      sorter: (a, b) => (a.name || '').localeCompare(b.name || '')
    },
    { title: '키(클래스)', dataIndex: 'eventKey', key: 'eventKey', ellipsis: true, 
      sorter: (a, b) => (a.eventKey || '').localeCompare(b.eventKey || '')
    },
    { title: '작업', key: 'actions', width: 460, render: (_, r) => {
      const isDeleted = (r.registerType||'') === '삭제';
      // 삭제 상태에서는 DB 삭제만 허용
      if (isDeleted) {
        return (
          <Space size={4}>
            <Popconfirm title="삭제" okText="삭제" cancelText="취소" onConfirm={() => handleDelete(r)}>
              <PermissionButton size="small" danger icon={<DeleteOutlined />} require={deleteEvent}>삭제</PermissionButton>
            </Popconfirm>
          </Space>
        );
      }
      return (
        <Space size={4} wrap>
          {r.eventId && (
            <>
              <PermissionButton size="small" icon={<EditOutlined />} onClick={() => openDrawer(r)} require={updateEvent}>수정</PermissionButton>
              <PermissionButton size="small" icon={<TeamOutlined />} onClick={() => openDrawer(r)} require={getListenersByEvent}>리스너</PermissionButton>
              {/* 일반 등록 이벤트에 대한 삭제 버튼 제거: 삭제는 '삭제' 상태에서만 수행 */}
            </>
          )}
          {!r.eventId && (
            <Tooltip title="미등록 이벤트입니다. 클릭하여 등록하세요.">
              <PermissionButton size="small" type="primary" onClick={() => registerEvent(r)} require={createEvent}>등록</PermissionButton>
            </Tooltip>
          )}
          {!r.eventId && (
            <Button size="small" icon={<EyeOutlined />} onClick={() => openDrawer(r)}>리스너 보기</Button>
          )}
        </Space>
      );
    }}
  ];

  const filteredData = useMemo(() => {
    const mode = filterMode;
    let list = data;
  if (mode === 'unregistered') list = list.filter(r => (r.registerType || '') === '미등록');
  else if (mode === 'registered') list = list.filter(r => (r.registerType || '') === '등록');
    else if (mode === 'deleted') list = list.filter(r => (r.registerType||'')==='삭제');
    else if (mode === 'changed') list = list.filter(r => (r.registerType||'')==='변경');

    const nq = (nameQuery||'').trim().toLowerCase();
    const eq = (explnQuery||'').trim().toLowerCase();
    if (nq) list = list.filter(r => (r.name||'').toLowerCase().includes(nq));
    if (eq) list = list.filter(r => (r.eventExpln||'').toLowerCase().includes(eq));
    return list;
  }, [data, filterMode, nameQuery, explnQuery]);

  const headerActions = (
    <Space wrap size={8} style={{ width: '100%' }}>
      <Button icon={<ReloadOutlined />} onClick={fetchData} disabled={permLoading || !canRead} loading={loading || bulkRegistering} size={screens.xs ? 'small' : 'middle'}>새로고침</Button>
      <Input allowClear placeholder="이벤트명" value={nameQuery} onChange={e=>setNameQuery(e.target.value)} style={{ width: screens.xs ? 120 : 180 }} size={screens.xs ? 'small' : 'middle'} />
      <Input allowClear placeholder="설명" value={explnQuery} onChange={e=>setExplnQuery(e.target.value)} style={{ width: screens.xs ? 120 : 220 }} size={screens.xs ? 'small' : 'middle'} />
      <PermissionButton type="primary" ghost icon={<PlusOutlined />} onClick={handleBatchRegister} disabled={bulkRegistering || !data.some(r => !r.eventId && selectedKeys.includes(r.key))} require={createEvent} size={screens.xs ? 'small' : 'middle'}>선택등록</PermissionButton>
      <PermissionButton onClick={handleBatchApplyUpdate} disabled={!data.some(r => selectedKeys.includes(r.key) && r.eventId && (r.registerType||'')==='변경') || permLoading || !canApply} loading={applyLoading} require={applyEventUpdate} size={screens.xs ? 'small' : 'middle'}>선택갱신</PermissionButton>
      <Button danger icon={<DeleteOutlined />} disabled={!data.some(r => selectedKeys.includes(r.key) && (r.registerType||'')==='삭제') || permLoading || !canDeleteMulti} onClick={handleBatchDelete} size={screens.xs ? 'small' : 'middle'}>선택삭제</Button>
    </Space>
  );
  // 타이틀 영역 라디오(이벤트/리스너) 구성
  const titleEl = (
    <span style={{ display:'inline-flex', alignItems:'center', gap:12 }}>
      이벤트 관리
      <Radio.Group value={activeTab} onChange={(e)=> setActiveTab(e.target.value)} size="small">
        <Radio.Button value="events">이벤트</Radio.Button>
        <Radio.Button value="listeners">리스너</Radio.Button>
      </Radio.Group>
    </span>
  );

  // Drawer 타이틀: 타이틀 옆에 사용여부/디스패치/저장 배치
  const drawerTitle = selected ? (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, width:'100%' }}>
      <span style={{ fontWeight: 600 }}>{selected.name}</span>
      <Space size={12} wrap>
        <div style={{ display:'inline-flex', alignItems:'center', gap:8 }}>
          <span style={{ color:'#666' }}>사용여부</span>
          <Switch checked={(editUseYn||'Y').toUpperCase()==='Y'} onChange={v => setEditUseYn(v ? 'Y' : 'N')} disabled={!selected.eventId || (selected.registerType||'')==='삭제' || permLoading || !canUpdate} />
        </div>
        <div style={{ display:'inline-flex', alignItems:'center', gap:8 }}>
          <span style={{ color:'#666' }}>디스패치</span>
          <Select
            size="middle"
            value={editDispatchType}
            onChange={v => setEditDispatchType(v)}
            style={{ width: 200 }}
            disabled={!selected.eventId || (selected.registerType||'')==='삭제' || permLoading || !canUpdate}
            options={[
              { value: 'SYNC', label: '동기' },
              { value: 'ASYNC', label: '비동기' },
              { value: 'ASYNC_AFTER_COMMIT', label: '비동기(커밋후)' },
            ]}
          />
        </div>
        <PermissionButton type="primary" onClick={submitEdit} disabled={!selected.eventId || (selected.registerType||'')==='삭제'} require={updateEvent}>저장</PermissionButton>
      </Space>
    </div>
  ) : '이벤트';

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <ManagementHeader icon={<ThunderboltOutlined />} title={titleEl} description="이벤트 메타의 설명, 사용여부, 디스패치, 등록/스캔 상태 및 리스너를 관리합니다." actions={activeTab==='events' ? headerActions : listenerHeaderActions} />

      {activeTab === 'events' ? (
        <>
          <Card size="small" styles={{ body: { padding: 0 } }}>
            <Tabs
              activeKey={filterMode}
              onChange={(key) => setFilterMode(key)}
              style={{ padding: '0 16px' }}
              items={[
                {
                  key: 'all',
                  label: '전체',
                  children: (
                    <Table
                      size="small"
                      rowKey={r=>r.key}
                      columns={columns}
                      dataSource={filteredData}
                      loading={loading || bulkRegistering}
                      pagination={{ pageSize: 15, showSizeChanger:false }}
                      scroll={{ x: 'max-content' }}
                      rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
                      onRow={(record) => ({
                        onClick: () => {
                          if ((record.registerType||'') === '삭제') return;
                          openDrawer(record);
                        }
                      })}
                    />
                  )
                },
                {
                  key: 'changed',
                  label: '변경',
                  children: (
                    <Table
                      size="small"
                      rowKey={r=>r.key}
                      columns={columns}
                      dataSource={filteredData}
                      loading={loading || bulkRegistering}
                      pagination={{ pageSize: 15, showSizeChanger:false }}
                      scroll={{ x: 'max-content' }}
                      rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
                      onRow={(record) => ({
                        onClick: () => {
                          if ((record.registerType||'') === '삭제') return;
                          openDrawer(record);
                        }
                      })}
                    />
                  )
                },
                {
                  key: 'unregistered',
                  label: '미등록',
                  children: (
                    <Table
                      size="small"
                      rowKey={r=>r.key}
                      columns={columns}
                      dataSource={filteredData}
                      loading={loading || bulkRegistering}
                      pagination={{ pageSize: 15, showSizeChanger:false }}
                      scroll={{ x: 'max-content' }}
                      rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
                      onRow={(record) => ({
                        onClick: () => {
                          if ((record.registerType||'') === '삭제') return;
                          openDrawer(record);
                        }
                      })}
                    />
                  )
                },
                {
                  key: 'registered',
                  label: '등록',
                  children: (
                    <Table
                      size="small"
                      rowKey={r=>r.key}
                      columns={columns}
                      dataSource={filteredData}
                      loading={loading || bulkRegistering}
                      pagination={{ pageSize: 15, showSizeChanger:false }}
                      scroll={{ x: 'max-content' }}
                      rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
                      onRow={(record) => ({
                        onClick: () => {
                          if ((record.registerType||'') === '삭제') return;
                          openDrawer(record);
                        }
                      })}
                    />
                  )
                },
                {
                  key: 'deleted',
                  label: '삭제',
                  children: (
                    <Table
                      size="small"
                      rowKey={r=>r.key}
                      columns={columns}
                      dataSource={filteredData}
                      loading={loading || bulkRegistering}
                      pagination={{ pageSize: 15, showSizeChanger:false }}
                      scroll={{ x: 'max-content' }}
                      rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
                      onRow={(record) => ({
                        onClick: () => {
                          if ((record.registerType||'') === '삭제') return;
                          openDrawer(record);
                        }
                      })}
                    />
                  )
                }
              ]}
            />
          </Card>

          {/* Drawer: 수정 + 리스너 통합 */}
          <Drawer
            title={drawerTitle}
            open={drawerOpen}
            onClose={() => { setDrawerOpen(false); setSelected(null); setListeners([]); setDbListeners([]); setUnifiedListeners([]); }}
            width={1000}
            footer={null}
          >
            {selected && (
              <>
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="이벤트명">{selected.name}</Descriptions.Item>
                  <Descriptions.Item label="키(클래스)">{selected.eventKey}</Descriptions.Item>
                  <Descriptions.Item label="등록구분">{selected.registerType || (selected.eventId ? '등록' : '미등록')}</Descriptions.Item>
                </Descriptions>
                <div style={{ marginTop: 12 }}>
                  <div style={{ marginBottom: 6 }}>설명</div>
                  <Input.TextArea value={editExpln} onChange={e=>setEditExpln(e.target.value)} rows={4} placeholder="이벤트 설명을 입력하세요" disabled={!selected.eventId || (selected.registerType||'')==='삭제' || permLoading || !canUpdate} />
                </div>

                <div style={{ marginTop: 24 }}>
                  <Typography.Title level={5} style={{marginTop:0, marginBottom: 0}}>리스너</Typography.Title>
                </div>
                <Table
                  size="small"
                  rowKey={(r, i) => `${r.beanName}-${r.methodName}-${i}`}
                  columns={unifiedListenerColumns}
                  dataSource={unifiedListeners}
                  loading={listenersLoading || dbListenersLoading}
                  pagination={{ pageSize: 12, showSizeChanger: false }}
                  scroll={{ x: 'max-content' }}
                />
                <CompareListenerModal open={compareOpen} onClose={closeCompare} row={compareRow} onApplied={onComparedApplied} />
              </>
            )}
          </Drawer>
        </>
      ) : (
        <GlobalListenersTab setHeaderActions={setListenerHeaderActions} />
      )}
    </div>
  );
};

export default EventManagement;
